import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import {StateParams} from "../../common";
import {ICommonQueryingReport} from "./commonQueryingReport";
import {Plant} from "../../models/navigation";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import template from './cavityEfficiencyReport.tpl.html';
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import CountsServiceName, {CountsService} from "../../services/counts/counts.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import UIUtilityServiceName, {UIUtilityService} from "../../services/utility/uiUtility.service";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";
import {States} from "../../shared/config/constant.module";
import Graph from './cavityEfficiencySummaryGraph.directive';

let requires = [LoaderServiceName, CountsServiceName, UIUtilityServiceName, NavigationServiceName, Graph];

export interface CavityEfficiencyReportParams extends StateParams {
  reportPlantId: string;
  reportShopId: string;
  startDate: string;
  endDate: string;
  autoRun: boolean;
  cavity: string;
}

interface CavityEfficiencyReportController extends ICommonQueryingReport {
  plants: Plant[];
  selectedPlant: Plant;
  selectedShop: apsViewModels.ShopNavigation;
  startDate: Date;
  endDate: Date;
  uiStartDate: Date;
  uiEndDate: Date;

  reportStartDate: Date;
  reportEndDate: Date;
  reportShopId: string;
  reportShopName: string;
  reportTimeZoneId: string;

  form: angular.IFormController;

  updatePlant(): void;
  formatTime(time: Date): string;
  generateReport(): void;
  generateDetailReport(summary: apsViewModels.CavityCountSummary): void;

  cavities: apsViewModels.CavityCountSummary[];
  detailResultsPretty: any[];
  searchPerformed: boolean;

  scopeWorkaround: {
    cavityBarClick(summary: apsViewModels.CavityCountSummary): void;
  };

  requirePlantSelection: boolean;
  requireShopSelection: boolean;
}

class CavityEfficiencyReportCtrl implements CavityEfficiencyReportController {
  plants: Plant[];
  selectedPlant: Plant;
  selectedShop: apsViewModels.ShopNavigation;
  startDate: Date;
  endDate: Date;
  uiStartDate: Date;
  uiEndDate: Date;

  reportStartDate: Date;
  reportEndDate: Date;
  reportShopId: string;
  reportShopName: string;
  reportTimeZoneId: string;

  form: angular.IFormController;

  cavities: apsViewModels.CavityCountSummary[];
  searchPerformed: boolean;

  detailResultsPretty: any[];
  detailGraphHeight: string;
  detailHorizontalBarHeight: number;

  loaderService: LoaderService;
  countsService: CountsService;
  uiUtilityService: UIUtilityService;
  $anchorScroll: angular.IAnchorScrollService;
  $location: angular.ILocationService;
  $state: angular.ui.IStateService;
  $stateParams: CavityEfficiencyReportParams;
  private $timeout: angular.ITimeoutService;

  scopeWorkaround: {
    cavityBarClick(summary: apsViewModels.CavityCountSummary): void;
  };

  requirePlantSelection: boolean;
  requireShopSelection: boolean;

  private $q: angular.IQService;

  constructor(navigationService: NavigationService,
              countsService: CountsService,
              uiUtilityService: UIUtilityService,
              loaderService: LoaderService,
              $anchorScroll: angular.IAnchorScrollService,
              $location: angular.ILocationService,
              $state: angular.ui.IStateService,
              $q: angular.IQService,
              $stateParams: CavityEfficiencyReportParams,
              $timeout: angular.ITimeoutService) {

    this.loaderService = loaderService;
    this.countsService = countsService;
    this.uiUtilityService = uiUtilityService;
    this.$anchorScroll = $anchorScroll;
    this.$location = $location;
    this.$state = $state;
    this.$q = $q;
    this.$stateParams = $stateParams;
    this.$timeout = $timeout;

    this.requirePlantSelection = true;
    this.requireShopSelection = true;

    this.detailGraphHeight = '500px';
    this.detailHorizontalBarHeight = 40;

    let now = moment().add(1, 'hours').startOf('hour');
    this.uiEndDate = now.toDate();
    this.uiStartDate = now.clone().subtract(1, 'days').toDate();

    let promise = navigationService.getNavigation().then((nav) => {
      this.plants = nav.plants();
      this.selectedPlant = nav.selectedPlant;
      this.updatePlant();
      this.selectedShop = nav.selectedShop;

      return this.loadState($stateParams);
    });

    loaderService.loadingPromise(promise);

    this.scopeWorkaround = {
      cavityBarClick: (summary: apsViewModels.CavityCountSummary) => {
        this.generateDetailReport(summary);
      }
      /*,
      formatTime: (time: Date): string => {
        return this.formatTime(time);
      }*/
    };

  }

  generateReport(): angular.IPromise<any> {
    if (this.form.$invalid) {
      return;
    }

    this.saveState(undefined);

    let promise = this.runReport();
    this.loaderService.loadingPromise(promise);
    return promise;
  }

  private runReport(): angular.IPromise<any> {
    this.reportShopId = this.selectedShop.id;
    this.reportShopName = this.selectedShop.name;
    this.reportStartDate = this.startDate;
    this.reportEndDate = this.endDate;
    this.reportTimeZoneId = this.selectedPlant.timeZoneId;

    this.cavities = [];
    this.detailResultsPretty = [];
    this.searchPerformed = false;

    return this.countsService.getCavityEfficiency(this.reportShopId,
      this.reportStartDate,
      this.reportEndDate)
      .then((cavities) => {
        this.cavities = cavities;
        this.searchPerformed = true;

        return this.$q.when();
      });
  }

  formatTime(time: Date): string {
    return DateUtilityService.formatTime(time, this.reportTimeZoneId);
  }

  updatePlant(): void {
    this.selectedShop = null;
  }

  generateDetailReport(summary: apsViewModels.CavityCountSummary): void {
    let cavity = summary ? summary.cavity : '';
    this.saveState(cavity);
    this.runDetailReport(summary);
  }

  private runDetailReport(summary: apsViewModels.CavityCountSummary): void {
    this.$timeout(() => {
      this.detailResultsPretty = [];
    });

    this.$timeout(() => {
      if (summary.details) {
        let totalDefectCount = _.sum(_.map(summary.details, detail => detail.rejectCount));
        _.forEach(summary.details, detail => {
          let rejectPct = parseFloat(((detail.rejectCount / totalDefectCount) * 100).toFixed(1));
          this.detailResultsPretty.push({
            'Defect': detail.defect,
            'Reject Pct': rejectPct,
            'Label': [ detail.defect, `${rejectPct}%`, detail.rejectCount ]
          });
        });

        this.detailGraphHeight = `${(100 + this.detailResultsPretty.length * this.detailHorizontalBarHeight)}px`;
      }
    });
  }

  loadState(params: CavityEfficiencyReportParams): angular.IPromise<any> {
    if (params.reportPlantId && params.reportShopId && params.reportShopId !== params.shopId) {
      let plant = _.find(this.plants, {id: params.reportPlantId});
      if (plant) {
        let shop = _.find(plant.shops, {id: params.reportShopId});
        if (shop) {
          this.selectedPlant = plant;
          this.selectedShop = shop;
        }
      }
    }

    DateUtilityService.setReportDates(this, params, this.$timeout);

    if (params.autoRun) {
      return this.runReport().then(() => {
        let targetSummary = _.find(this.cavities, {cavity: params.cavity});
        if (targetSummary) {
          this.runDetailReport(targetSummary);
          this.$location.hash('detailResultsMarker');
          this.$anchorScroll();
        }
      });
    }

    return this.$q.when();
  }

  saveState(cavity: string): void {
    let reportShopId = this.selectedShop ? this.selectedShop.id : undefined;
    if (reportShopId === this.$stateParams.shopId) {
      reportShopId = undefined;
    }
    let params: CavityEfficiencyReportParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: (reportShopId && this.selectedPlant) ? this.selectedPlant.id : undefined,
      reportShopId: reportShopId,
      startDate: this.startDate ? this.startDate.toISOString() : undefined,
      endDate: this.endDate ? this.endDate.toISOString() : undefined,
      autoRun: true,
      cavity: cavity
    };

    this.$state.transitionTo(this.$state.current.name, params);
  }
}

export default angular.module('aps.controller.cavityEfficiencyReport', requires)
  .config(function($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.CAVITY_EFFICIENCY,
      url: '/admin/reports/cavityEfficiency?startDate&endDate&autoRun&reportPlantId&reportShopId&cavity',
      params: {
        startDate: {dynamic: true},
        endDate: {dynamic: true},
        autoRun: {dynamic: true},
        reportPlantId: {dynamic: true},
        reportShopId: {dynamic: true},
        cavity: {dynamic: true}
      },
      controller: 'CavityEfficiencyReportCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Cavity Efficiency Report'
      }
    });
  })
  .controller('CavityEfficiencyReportCtrl', CavityEfficiencyReportCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/reports/cavityEfficiencyReport.controller.ts